import { MetrixCourse, MetrixCompetition } from '../types/metrix';
import { Course } from '../types/course';
import { collection, getDocs, getDoc, doc, Timestamp } from 'firebase/firestore';
import { db } from '../config/firebase';

// Metrix API -asetukset on siirretty MetrixCourseDetailService-luokkaan

interface FetchCompetitionsParams {
  startDate: Date;
  endDate: Date;
  countryCode?: string;
}

export class MetrixService {
  private static instance: MetrixService;

  private constructor() {}

  public static getInstance(): MetrixService {
    if (!MetrixService.instance) {
      MetrixService.instance = new MetrixService();
    }
    return MetrixService.instance;
  }

  private processMetrixCourse(course: MetrixCourse, layouts: MetrixCourse[]): Course {
    let lat = 0, lng = 0;

    try {
      // Process coordinates
      if (course.Location && typeof course.Location === 'object') {
        const locationObj = course.Location as { lat?: string | number; lng?: string | number };
        if (locationObj.lat && locationObj.lng) {
          lat = Number(locationObj.lat);
          lng = Number(locationObj.lng);
        }
      } else if (course.X && course.Y) {
        // Metrix API:ssa X on leveysaste (latitude) ja Y on pituusaste (longitude)
        lat = Number(course.X); // X -> latitude
        lng = Number(course.Y); // Y -> longitude
      }

      // Validate coordinates
      if (isNaN(lat) || isNaN(lng) || !isFinite(lat) || !isFinite(lng)) {
        //console.log('Invalid coordinates:', course.Name, { lat, lng });
        lat = 0;
        lng = 0;
      }

      // Create course object with layouts
      return {
        id: course.ID,
        name: course.Name,
        coordinates: { lat, lng },
        holes: Number(course.Holes) || 0, // Ei oleteta 18 väylää jos tieto puuttuu
        par: Number(course.Par) || undefined,
        ParentID: course.ParentID,
        Type: course.Type,
        Enddate: course.Enddate,
        layouts: layouts.map((layout: MetrixCourse) => ({
          id: layout.ID,
          name: layout.Name,
          coordinates: { lat: Number(layout.X) || 0, lng: Number(layout.Y) || 0 }, // X = lat, Y = lng Metrix API:ssa
          holes: Number(layout.Holes) || 0, // Ei oleteta 18 väylää jos tieto puuttuu
          par: Number(layout.Par) || undefined,
          ParentID: layout.ParentID,
          Type: layout.Type,
          Enddate: layout.Enddate
        }))
      };
    } catch (error: unknown) {
      console.error('Error processing course:', course.Name, error);
      return {
        id: course.ID,
        name: course.Name,
        coordinates: { lat: 0, lng: 0 },
        holes: 0, // Ei oleteta 18 väylää jos tieto puuttuu
        layouts: []
      };
    }
  }

  /**
   * Fetch courses from Firestore
   * @param forcedFetch If true, bypasses all caching and forces direct Firestore fetch
   * @returns Promise with array of courses
   */
  public async fetchMetrixCourses(forcedFetch: boolean = false): Promise<Course[]> {
    try {
      // Jos forceDirectFetch on true, ohita kaikki välimuistit
      if (forcedFetch) {

      } else {
        // V1-arkkitehtuurin mukainen logiikka välimuistin käyttöön
        // 1. Tarkista onko window.coursesData saatavilla
        if (window.coursesData && window.coursesData.length > 0) {

          return window.coursesData;
        }
        
        // 2. Tarkista localStorage välimuisti
        const cachedDataStr = localStorage.getItem('ratahaukka_courses_data');
        if (cachedDataStr) {
          try {
            const parsedData = JSON.parse(cachedDataStr);
            if (Array.isArray(parsedData) && parsedData.length > 0) {

              return parsedData;
            }
          } catch (e) {
            console.warn('MetrixService: Error parsing cached data', e);
          }
        }
      }
      
      // AINA HAE RADAT SUORAAN FIRESTORESTA

      const coursesRef = collection(db, 'courses');
      const snapshot = await getDocs(coursesRef);
      
      if (snapshot.empty) {
        console.error('MetrixService: CRITICAL ERROR: No courses found in Firestore!');
        return [];
      }
      

      
      // Varmistetaan että kaikki kurssit ladataan yhtenäisesti
      let allCourses = snapshot.docs.map(doc => {
        const data = doc.data() as MetrixCourse;
        // Varmistetaan että ID on aina saatavilla
        if (!data.ID) {
          data.ID = doc.id;
        }
        return data;
      });
      
      // Loggaus ennen suodatuksia

      
      // Ratojen tyyppisuodatus: parentit (Type 1) + layoutit (Type 2)
      const parentCourses = allCourses.filter((course: MetrixCourse) => 
        course.Type === '1' || !course.Type // Hyväksy myös kurssit ilman tyyppiä
      );
      
      const layouts = allCourses.filter((course: MetrixCourse) => 
        course.Type === '2' && course.ParentID // Varmista että layoutilla on ParentID
      );

      // Process parent courses with their layouts
      const processedCourses = parentCourses.map((course: MetrixCourse) => {
        const courseLayouts = layouts.filter((layout: MetrixCourse) => 
          layout.ParentID === course.ID
        );
        return this.processMetrixCourse(course, courseLayouts);
      });

      // Tallenna välimuistiin ja window-objektiin V1-yhteensopivuutta varten
      if (processedCourses.length > 0) {

        localStorage.setItem('ratahaukka_courses_data', JSON.stringify(processedCourses));
        window.coursesData = processedCourses;
      }


      return processedCourses;
    } catch (error) {
      console.error('Error fetching courses:', error);
      return [];
    }
  }

  public async fetchMetrixCompetitions({
    startDate,
    endDate,
    countryCode = 'FI'
  }: FetchCompetitionsParams): Promise<MetrixCompetition[]> {
    // Validate date range (must be less than 365 days)
    const daysDiff = Math.ceil((endDate.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24));
    if (daysDiff > 365) {
      throw new Error('Date range cannot exceed 365 days');
    }

    try {
      //console.log('Fetching competitions from Firestore...');
      const competitionsRef = collection(db, 'competitions');
      const snapshot = await getDocs(competitionsRef);
      
      if (snapshot.empty) {
        //console.log('No competitions found in Firestore');
        return [];
      }
      
      //console.log(`Found ${snapshot.docs.length} competitions in Firestore`);
      
      // Convert Firestore documents to competitions
      let competitions = snapshot.docs.map(doc => doc.data() as MetrixCompetition);
      
      // Filter by date range
      competitions = competitions.filter(comp => {
        const compDate = new Date(comp.Date);
        return compDate >= startDate && compDate <= endDate;
      });
      
      //console.log(`Filtered to ${competitions.length} competitions in date range`);
      
      // Filter out competitions without CourseID
      //const totalBeforeFilter = competitions.length;
      const filtered = competitions.filter(comp => comp.CourseID !== null);

      if (filtered.length > 0) {
        //console.log('Metrix competitions data:', {
        //  total: totalBeforeFilter,
        //  filtered: filtered.length,
        //  withCourseId: filtered.length
        //});
      }

      return filtered;
    } catch (error) {
      console.error('Error fetching competitions:', error);
      return [];
    }
  }

  /**
   * Fetch the sync status for Metrix data in Firestore
   * @returns Promise with the sync status information
   */
  public async getSyncStatus(): Promise<{
    lastUpdate: Date | null;
    competitionsCount: number;
    coursesCount: number;
    layoutsCount: number;
  }> {
    try {
      // Hae kaikkien synkronointi-vaiheiden metatiedot
      const [compMeta, coursesMeta, layoutsMeta, mainMeta] = await Promise.all([
        getDoc(doc(db, 'metadata', 'metrix_competitions_sync')),
        getDoc(doc(db, 'metadata', 'metrix_courses_basic_sync')),
        getDoc(doc(db, 'metadata', 'metrix_layouts_sync')),
        getDoc(doc(db, 'metadata', 'metrix_sync'))
      ]);
      
      // Oletusarvot
      const result = {
        lastUpdate: null as Date | null,
        competitionsCount: 0,
        coursesCount: 0,
        layoutsCount: 0
      };
      
      // Parse Firebase timestamp to Date
      const parseTimestamp = (ts: any): Date | null => {
        if (!ts) return null;
        if (ts instanceof Timestamp) return ts.toDate();
        if (typeof ts === 'string') return new Date(ts);
        return null;
      };
      
      // Päivitä tiedot jos saatavilla
      if (mainMeta.exists()) {
        const data = mainMeta.data();
        result.lastUpdate = parseTimestamp(data.lastScheduledSync || data.lastUpdate);
      }
      
      if (compMeta.exists()) {
        const data = compMeta.data();
        result.competitionsCount = data.competitionsCount || 0;
      }
      
      if (coursesMeta.exists()) {
        const data = coursesMeta.data();
        result.coursesCount = data.parentCoursesCount || 0;
      }
      
      if (layoutsMeta.exists()) {
        const data = layoutsMeta.data();
        result.layoutsCount = data.layoutsCount || 0;
      }
      
      return result;
    } catch (error) {
      console.error('Error fetching sync status:', error);
      return {
        lastUpdate: null,
        competitionsCount: 0,
        coursesCount: 0,
        layoutsCount: 0
      };
    }
  }

  /**
   * Käytöstä poistuva metodi - käytä getSyncStatus-metodia
   * @deprecated Use getSyncStatus() instead
   */
  public async getLastUpdateTimestamp(): Promise<Date | null> {
    try {
      const status = await this.getSyncStatus();
      return status.lastUpdate;
    } catch (error) {
      console.error('Error fetching last update timestamp:', error);
      return null;
    }
  }

  private formatDate(date: Date): string {
    return date.toISOString().split('T')[0];
  }

  private formatDateTime(date: Date): string {
    return date.toISOString();
  }
}